import React from 'react';
import { Handle, Position } from 'reactflow';
import { Repeat } from 'lucide-react';
import { cn } from '@/lib/utils';

interface LoopNodeData {
  label?: string;
  iterations?: number;
  variable?: string;
}

export default function LoopNode({ 
  data, 
  selected 
}: { 
  data: LoopNodeData; 
  selected?: boolean 
}) {
  return (
    <div className={cn(
      "min-w-[160px] rounded-lg pb-8", // Extra padding for handle labels
      "bg-card",
      "border-2 transition-all duration-fast",
      selected
        ? "border-category-diagnostics shadow-glow-focus"
        : "border-category-diagnostics/50 dark:border-category-diagnostics/30 shadow-md hover:shadow-lg"
    )}>
      {/* Connection Handle */}
      <Handle
        type="target"
        position={Position.Top}
        className="!w-3 !h-3 !border-2 !bg-category-diagnostics !border-category-diagnostics"
        style={{ top: '-6px' }}
      />

      {/* Node Header */}
      <div className="flex items-center gap-2 p-3 border-b border-border dark:border-border-subtle">
        <div className="w-6 h-6 rounded bg-category-diagnostics-bg flex items-center justify-center">
          <Repeat className="w-3.5 h-3.5 text-category-diagnostics" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-foreground">
            Loop
          </div>
          <div className="text-xs text-muted-foreground/70">
            {data.iterations || 1} iteration{data.iterations !== 1 ? 's' : ''}
          </div>
        </div>
      </div>

      {/* Node Body */}
      <div className="p-3">
        {data.variable && (
          <div className="text-xs">
            <span className="text-muted-foreground">Variable: </span>
            <span className="text-foreground font-mono">${data.variable}</span>
          </div>
        )}
      </div>

      {/* Branch Handles */}
      <div className="relative">
        {/* Loop Handle */}
        <Handle
          type="source"
          position={Position.Bottom}
          id="loop"
          className="!w-3 !h-3 !border-2 !bg-accent-blue !border-accent-blue"
          style={{ left: '30%', bottom: '-6px' }}
        />
        <div className="absolute text-xs font-medium text-accent-blue" style={{ left: '30%', bottom: '-24px', transform: 'translateX(-50%)' }}>
          Loop
        </div>

        {/* Continue Handle */}
        <Handle
          type="source"
          position={Position.Bottom}
          id="continue"
          className="!w-3 !h-3 !border-2 !bg-text-tertiary-light dark:!bg-text-tertiary !border-text-tertiary-light dark:!border-text-tertiary"
          style={{ left: '70%', bottom: '-6px' }}
        />
        <div className="absolute text-xs font-medium text-muted-foreground/70" style={{ left: '70%', bottom: '-24px', transform: 'translateX(-50%)' }}>
          Continue
        </div>
      </div>
    </div>
  );
}

